import React, { useState } from "react";
import { Image, Badge, Tooltip } from "antd";
import { PlayCircleOutlined } from "@ant-design/icons";
import MediaPlayer from "../media-player/media-player";
import { MediaType } from "../../constants/field-values";
import { getFileIcon } from "utils/files-management";
import { useVideoThumbnail } from "hooks/useVideoThumbnail";

interface FileAttachment {
  id?: string;
  fileName: string;
  mimeType: string;
  publicUrl?: string;
  fileType: string;
  fileKey?: string;
  gsUri?: string;
}

interface MediaSibling {
  src: string;
  mediaType: MediaType;
}

interface FileAttachmentThumbnailProps {
  attachment: FileAttachment;
  size?: "small" | "medium" | "large";
  showFileName?: boolean;
  onClick?: () => void;
  siblings?: MediaSibling[];
  siblingIndex?: number;
}

export const FileAttachmentThumbnail: React.FC<
  FileAttachmentThumbnailProps
> = ({ attachment, size = "medium", showFileName = true, onClick, siblings, siblingIndex }) => {
  const [mediaPlayerOpen, setMediaPlayerOpen] = useState(false);
  const [mediaPlayerType, setMediaPlayerType] = useState<MediaType>(MediaType.Image);
  const FileIcon = getFileIcon(attachment.mimeType);

  const isVideo = attachment.mimeType.startsWith("video/");
  const isImage = attachment.mimeType.startsWith("image/");
  const isAudio = attachment.mimeType.startsWith("audio/");
  const isPdf = attachment.mimeType === "application/pdf";

  // Only mp4/webm/ogg are natively playable in all major browsers
  const browserCanPlayVideo =
    attachment.mimeType === "video/mp4" ||
    attachment.mimeType === "video/webm" ||
    attachment.mimeType === "video/ogg";

  // Generate canvas thumbnail via shared hook (handles cleanup and Firefox compat)
  const videoThumbnail = useVideoThumbnail(
    isVideo && browserCanPlayVideo ? attachment.publicUrl : null,
    1
  );

  const getFileExtension = () => {
    const parts = attachment.fileName.split(".");
    return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : "FILE";
  };

  const getSizeClass = () => {
    switch (size) {
      case "small": return "file-attachment-small";
      case "large": return "file-attachment-large";
      default: return "file-attachment-medium";
    }
  };

  const openMediaPlayer = (type: MediaType) => {
    setMediaPlayerType(type);
    setMediaPlayerOpen(true);
  };

  const handleThumbnailClick = () => {
    if (onClick) { onClick(); return; }
    if (!attachment.publicUrl) return;

    if (isImage) {
      openMediaPlayer(MediaType.Image);
    } else if (isVideo) {
      openMediaPlayer(MediaType.Video);
    } else if (isAudio) {
      openMediaPlayer(MediaType.Audio);
    } else if (isPdf) {
      openMediaPlayer(MediaType.Document);
    } else {
      const link = document.createElement("a");
      link.href = attachment.publicUrl;
      link.download = attachment.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // --- IMAGE ---
  if (isImage && attachment.publicUrl) {
    return (
      <>
        <div className={`file-attachment-thumbnail ${getSizeClass()}`}>
          <Tooltip title={`Click to view ${attachment.fileName}`}>
            <div
              className="file-attachment-clickable-image"
              onClick={handleThumbnailClick}
            >
              <Image
                src={attachment.publicUrl}
                alt={attachment.fileName}
                className="file-attachment-image"
                preview={false}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
              />
            </div>
          </Tooltip>
          {showFileName && (
            <div className="file-attachment-name" title={attachment.fileName}>
              {attachment.fileName}
            </div>
          )}
        </div>
        {mediaPlayerOpen && (
          <MediaPlayer
            src={attachment.publicUrl}
            mediaType={mediaPlayerType}
            open={mediaPlayerOpen}
            onCancel={() => setMediaPlayerOpen(false)}
            siblings={siblings}
            siblingIndex={siblingIndex}
          />
        )}
      </>
    );
  }

  // --- VIDEO (supported + unsupported) ---
  if (isVideo && attachment.publicUrl) {
    const isSupported = browserCanPlayVideo;
    const tooltipText = isSupported
      ? `Click to play ${attachment.fileName}`
      : `${getFileExtension()} — click to attempt playback`;

    const videoThumbClass = `file-attachment-video-thumb ${isSupported ? 'file-attachment-video-thumb--supported' : 'file-attachment-video-thumb--unsupported'}`;
    const fallbackClass = `file-attachment-video-fallback ${!isSupported ? 'file-attachment-video-fallback--unsupported' : ''}`;
    const playIconClass = `file-attachment-play-icon ${!isSupported ? 'file-attachment-play-icon--unsupported' : ''}`;

    return (
      <>
        <div className={`file-attachment-thumbnail ${getSizeClass()}`}>
          <Tooltip title={tooltipText}>
            <div
              className={videoThumbClass}
              onClick={handleThumbnailClick}
            >
              {/* Frame thumbnail or dark fallback */}
              {videoThumbnail ? (
                <img
                  src={videoThumbnail}
                  alt="Video thumbnail"
                  className="file-attachment-video-thumbnail-img"
                />
              ) : (
                <div className={fallbackClass}>
                  <FileIcon />
                </div>
              )}

              {/* Play button + optional unsupported label overlay */}
              <div className="file-attachment-video-overlay">
                <PlayCircleOutlined className={playIconClass} />
                {!isSupported && (
                  <div className="file-attachment-unsupported-label">
                    Format not supported
                    <br />
                    Converting on server…
                  </div>
                )}
              </div>
            </div>
          </Tooltip>
          {showFileName && (
            <div className="file-attachment-name" title={attachment.fileName}>
              {attachment.fileName}
            </div>
          )}
        </div>
        {mediaPlayerOpen && (
          <MediaPlayer
            src={attachment.publicUrl}
            mediaType={MediaType.Video}
            open={mediaPlayerOpen}
            onCancel={() => setMediaPlayerOpen(false)}
            siblings={siblings}
            siblingIndex={siblingIndex}
          />
        )}
      </>
    );
  }

  // --- AUDIO, PDF, and other file types (icon thumbnail) ---
  const canPreview = isAudio || isPdf;
  const iconTooltip = canPreview
    ? `Click to open ${attachment.fileName}`
    : `Click to download ${attachment.fileName}`;

  return (
    <>
      <div className={`file-attachment-thumbnail ${getSizeClass()}`}>
        <Tooltip title={iconTooltip}>
          <div
            className="file-attachment-icon-container"
            onClick={handleThumbnailClick}
          >
            <div className="file-attachment-icon">
              <FileIcon />
            </div>
            {showFileName && (
              <div
                className="file-attachment-name"
                title={attachment.fileName}
              >
                {attachment.fileName}
              </div>
            )}
            <Badge
              count={getFileExtension()}
              style={{ backgroundColor: "#1890ff", fontSize: "10px", fontWeight: "500" }}
              className="file-attachment-ext-badge"
            />
          </div>
        </Tooltip>
      </div>
      {mediaPlayerOpen && attachment.publicUrl && (
        <MediaPlayer
          src={attachment.publicUrl}
          mediaType={mediaPlayerType}
          open={mediaPlayerOpen}
          onCancel={() => setMediaPlayerOpen(false)}
          siblings={siblings}
          siblingIndex={siblingIndex}
        />
      )}
    </>
  );
};
